import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type SessionEvidence } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { SessionEvidenceRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const SESSION_EVIDENCE = 'sessionEvidence';

const converter = buildConverter<SessionEvidence>(
  (data) =>
    ({
      ...data,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
    }) as SessionEvidence,
  (model) => ({ ...model, createdAt: serverTimestamp() })
);

export class FirestoreSessionEvidenceRepository implements SessionEvidenceRepository {
  async findAll(): Promise<SessionEvidence[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SESSION_EVIDENCE),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<SessionEvidence | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, SESSION_EVIDENCE, id).withConverter(converter));
    return snap.data();
  }

  async findBySessionId(sessionId: string): Promise<SessionEvidence[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, SESSION_EVIDENCE),
      where('sessionId', '==', sessionId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(item: SessionEvidence): Promise<SessionEvidence> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...item, environment: getCurrentEnvironment() };
    const existing = await this.findById(item.id);
    await setDoc(doc(db, SESSION_EVIDENCE, item.id).withConverter(converter), data);
    return { ...data, createdAt: existing?.createdAt ?? item.createdAt };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, SESSION_EVIDENCE, id));
  }
}

export const createFirestoreSessionEvidenceRepository = (): FirestoreSessionEvidenceRepository =>
  new FirestoreSessionEvidenceRepository();
